import type { Rule } from 'antd-v5/es/form';
import type { DateTime } from 'luxon';

import type { RankVersion } from '@appTypes/common/versions';
import type { RankRequirement, YouthRankRequirement } from '@appTypes/esb';
import type { AdvancementAuditInfo } from '@shared/components/AdvancementAudit/AdvancementAuditTooltip';

import type {
  AdvancementAction,
  FormField,
  SupportedAdvancementType,
} from '../AdvancementDetails/types';

/**
 * One requirement, catalog text merged with the youth's progress and nested by
 * `parentRequirementId`.
 *
 * The two sources disagree on primitives - the catalog sends `"True"` and `"467"`
 * where the youth route sends `true` and `467` - so everything here is the
 * coerced form produced by `mergeRequirements`
 */
export interface RequirementNode extends AdvancementAuditInfo {
  id: number;
  versionId: number;
  /** Raw HTML; sanitize before rendering */
  name: string;
  short?: string;
  listNumber?: string;
  requirementNumber?: string;
  sortOrder?: string;
  footer?: string;
  required: boolean;
  optional?: boolean;
  parentRequirementId: number | null;
  /** How many children satisfy this parent; 0 for a leaf */
  childrenRequired: number;
  disabledOnQuickEntry: boolean;
  /** Inconsistent from the API; derive with `advancementStatus.getStatus` */
  status?: string | null;
  dateStarted?: string | null;
  dateCompleted?: string | null;
  /** A 0-1 fraction, not a percentage */
  percentCompleted?: number | null;
  /** Set by the tree builder; children of this requirement */
  requirements?: RequirementNode[];
  /**
   * Everything else the two sources carry (linked merit badges, adventures,
   * Sea Scout electives, `eagleMBRequired`, ...). Kept loose because
   * `isUpdateableRequirement` reads several of them and consumers narrow the
   * rest themselves via `overrideContent`
   */
  [key: string]: unknown;
}

/** The per-requirement form values, one entry per row under `requirements` */
export interface RequirementFormValues {
  [FormField.CompletionDate]: DateTime | null;
  [FormField.Approved]: boolean;
}

/** The shape the consuming `<Form>` seeds as `initialValues` */
export interface RequirementsFormData {
  requirements: Record<string, RequirementFormValues>;
}

/**
 * What a consumer's `disableRequirement` may return. `true` disables every
 * control on the row; an object disables only the named actions
 */
export type RequirementDisableResult =
  | boolean
  | Partial<Record<AdvancementAction, boolean>>;

/** Resolved per-row policy, after every layer has been folded in */
export interface RequirementRowPolicy {
  /** Controls rendered but greyed */
  disabled: Partial<Record<AdvancementAction, boolean>>;
  /** The approve control is not rendered at all for this requirement */
  hideApprove: boolean;
  /** No controls at all - parents, linked advancements, display-only rows */
  readOnly: boolean;
}

export interface VersionOption extends RankVersion {
  /** `Active` / `Past` / `Archived`, resolved against today */
  timeFrame: VersionTimeFrame;
}

export enum VersionTimeFrame {
  Active = 'active',
  Past = 'past',
  Archived = 'archived',
}

export interface RequirementsRendererProps {
  requirements: RequirementNode[];
  advancementType: SupportedAdvancementType;
  loading?: boolean;
  /** The row currently saving; only that row shows a spinner */
  savingRequirementId?: number | null;
  /**
   * An operation is in flight that the whole list must wait out - a version
   * switch, say. Shows a spinner over the list and greys every control without
   * hiding any. Distinct from `disabledActions`, which is a permission answer
   */
  busy?: boolean;
  /** Greyed on every row - the page-level action policy */
  disabledActions?: AdvancementAction[];
  /** Whether the viewer is a youth acting on their own record */
  isYouthEditor?: boolean;
  /**
   * Additive per-row disabling. It can only take a control away; returning
   * `false` never re-enables one another layer disabled
   */
  disableRequirement?: (
    id: number,
    requirement: RequirementNode,
  ) => RequirementDisableResult;
  /** Replaces the requirement body instead of the API's HTML */
  overrideContent?: (
    id: number,
    requirement: RequirementNode,
  ) => React.ReactNode;
  /** Rendered below the requirement */
  extraContent?: (id: number, requirement: RequirementNode) => React.ReactNode;
  /**
   * Replaces the built-in bound on the completion date, which is "not in the future".
   * Return true for a date the picker should refuse
   */
  disabledDate?: (date: DateTime, requirement: RequirementNode) => boolean;
  /** Appended after the built-in no-future rule on every completion date */
  completionDateRules?: Rule[];
  /**
   * Rejecting marks the save as failed: the row keeps its edit and stays
   * re-savable, so the handler must not swallow its own errors
   */
  onSaveRequirement?: (
    id: number,
    values: RequirementFormValues,
    /** The top-level ancestor, which the save payload needs for the parent cascade */
    rootRequirement: RequirementNode,
  ) => void | Promise<unknown>;
  /**
   * The version's own notes, rendered under the list. Both are raw HTML and
   * either may be empty. They belong to the *version*, so they change with it
   */
  notes?: { header?: string | null; footer?: string | null };
  /** Omit to hide the version picker entirely */
  version?: {
    options: VersionOption[];
    selectedVersionId?: number;
    disabled?: boolean;
    onChange: (versionId: number) => void;
    changing?: boolean;
  };
}

/** The raw inputs `mergeRequirements` joins */
export type CatalogRequirement = RankRequirement;
export type YouthRequirement = YouthRankRequirement;
